import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter } from '../../../components/common/FilterComponent';
import PaginationComponent from '../../../components/common/PaginationComponent';
import DeleteConfirmationModal from '../../../components/common/DeleteConfirmationModal';
import { questionBaseService, Question } from '../../../services/questionBaseService';
import { areaService, Area } from '../../../services/areaService';

const QuestionAdminPage: React.FC = () => {
    const history = useHistory();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [areas, setAreas] = useState<Area[]>([]);
    const [areasLoading, setAreasLoading] = useState<boolean>(true);
    const [areasError, setAreasError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [showAddQuestionModal, setShowAddQuestionModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [formData, setFormData] = useState<Partial<Question>>({});
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'question' | 'area'>('question');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [activeFilters, setActiveFilters] = useState<{
        titulo?: string;
        areaId?: string;
    }>({});

    const filterOptions: FilterOption[] = [
        { value: 'question', label: 'Questão' },
        { value: 'area', label: 'Área' }
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await questionBaseService.getQuestions({
                    page: currentPage,
                    size: pageSize,
                    ...activeFilters
                });
                setQuestions(response.content);
                setTotalElements(response.totalElements);
                setTotalPages(response.totalPages);
                setError(null);
            } catch (err) {
                setError('Failed to fetch data. Please try again later.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentPage, pageSize, activeFilters]);

    useEffect(() => {
        // Fetch all areas on mount
        const fetchAreas = async () => {
            try {
                setAreasLoading(true);
                setAreasError(null);
                const response = await areaService.getAreas();
                setAreas(response.map(area => ({
                    area_id: area.area_id,
                    nome: area.nome,
                    status: area.status
                })));
            } catch (err) {
                setAreasError('Erro ao buscar áreas.');
                setAreas([]);
            } finally {
                setAreasLoading(false);
            }
        };
        fetchAreas();
    }, []);

    const handleBack = () => {
        history.push(`/admin`);
    };

    const handleAddQuestionClick = () => {
        setFormData({
            titulo: '',
            descricao: '',
            areaId: undefined,
            itemIds: [],
        });
        setShowAddQuestionModal(true);
    };

    const handleEditClick = (question: Question) => {
        setSelectedQuestion(question);
        setFormData({
            titulo: question.titulo,
            descricao: question.descricao,
            areaId: question.areaId,
        });
        setShowEditModal(true);
    };

    const handleItemsClick = (question: Question) => {
        history.push(`/admin/questoes/${question.questaoBaseId}/itens`);
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
        setSelectedQuestion(null);
        setFormData({});
    };

    const handleDeleteClick = (question: Question) => {
        setSelectedQuestion(question);
        setShowDeleteModal(true);
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setSelectedQuestion(null);
        setIsDeleting(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedQuestion) return;

        try {
            setIsDeleting(true);
            await questionBaseService.deleteQuestion(selectedQuestion.questaoBaseId!);

            // Remove the question from the local state
            setQuestions(prevQuestions =>
                prevQuestions.filter(q => q.questaoBaseId !== selectedQuestion.questaoBaseId)
            );

            setSuccessMessage('Questão excluída com sucesso!');
            handleDeleteModalClose();
        } catch (err) {
            setError('Erro ao excluir questão. Por favor, tente novamente.');
            console.error('Error deleting question:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitEdit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (selectedQuestion) {
                const updatedQuestion = await questionBaseService.updateQuestion(selectedQuestion.questaoBaseId, {
                    titulo: formData.titulo || '',
                    descricao: formData.descricao || '',
                    areaId: formData.areaId,
                    tipoBase: "KATRU"
                });

                // Update the questions list with the updated question
                setQuestions(prevQuestions =>
                    prevQuestions.map(question =>
                        question.questaoBaseId === selectedQuestion.questaoBaseId ? updatedQuestion : question
                    )
                );
            }

            handleEditModalClose();
        } catch (err) {
            setError('Failed to save question. Please try again later.');
            console.error('Error saving question:', err);
        }
    };

    const getAreaName = (areaId: string) => {
        return areas.find(area => area.area_id === areaId)?.nome || 'Área não encontrada';
    };

    // Function to apply filters (called when user clicks search or presses Enter)
    const handleFilterApply = (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'question' | 'area';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterText.trim(),
            label: filterOption?.label || typedFilterType
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');

        // Apply the filter to backend
        applyFiltersToBackend([...updatedFilters, newAppliedFilter]);
    };

    // Function to remove a specific filter
    const handleFilterRemove = (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
        applyFiltersToBackend(updatedFilters);
    };

    // Function to apply filters to backend
    const applyFiltersToBackend = (filters: AppliedFilter[]) => {
        // Map filter type to backend parameter
        const newFilters: any = {};

        // Only apply filters if there are any
        if (filters.length > 0) {
            filters.forEach(filter => {
                if (filter.type === 'question') {
                    newFilters.titulo = filter.value;
                } else if (filter.type === 'area') {
                    // Find area ID by name
                    const area = areas.find(a => a.nome.toLowerCase().includes(filter.value.toLowerCase()));
                    if (area) {
                        newFilters.areaId = area.area_id;
                    }
                }
            });
        }

        setActiveFilters(newFilters);
        setCurrentPage(0); // Reset to first page when applying filters
    };

    // Function to clear filters
    const handleFilterClear = () => {
        setFilterText('');
        setAppliedFilters([]);
        setActiveFilters({});
        setCurrentPage(0); // Reset to first page when clearing filters
    };

    // Pagination handlers
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0); // Reset to first page when changing page size
    };

    const handleAddQuestionModalClose = () => {
        setShowAddQuestionModal(false);
        setFormData({});
    };

    const handleSubmitAddQuestion = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            await questionBaseService.createQuestion({
                areaId: formData.areaId || '',
                titulo: formData.titulo || '',
                descricao: formData.descricao || '',
                tipoBase: "KATRU",
                itemIds: []
            });

            // Refresh the questions list to get the updated data
            const response = await questionBaseService.getQuestions({
                page: currentPage,
                size: pageSize
            });
            setQuestions(response.content);
            setTotalElements(response.totalElements);
            setTotalPages(response.totalPages);

            handleAddQuestionModalClose();
        } catch (err) {
            setError('Failed to add question. Please try again later.');
            console.error('Error adding question:', err);
        }
    };

    if (loading) {
        return <div className="text-center p-5">Loading...</div>;
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={handleBack}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="dashboard-header mb-4 d-flex flex-row justify-content-between">
                <h2 className="mb-0 text-start">Gerenciamento de Questões</h2>
                <Button variant="primary" onClick={handleAddQuestionClick}>
                    Adicionar Questão
                </Button>
            </div>

            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span>🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'question' | 'area')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={`Buscar por ${filterType === 'question' ? 'questão' : 'área'}...`}
                    />
                </div>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
                    {successMessage}
                </Alert>
            )}

            {areasError && <Alert variant="danger">{areasError}</Alert>}

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Área</th>
                            <th>Título</th>
                            <th>Descrição</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {questions.map((question, index) => (
                            <tr key={question.questaoBaseId || `question-${index}`}>
                                <td>
                                    {areasLoading ? (
                                        <span className="text-muted">Carregando...</span>
                                    ) : (
                                        getAreaName(question.areaId)
                                    )}
                                </td>
                                <td>{question.titulo}</td>
                                <td>{question.descricao}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleEditClick(question)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            className="d-flex align-items-center border border-secondary text-secondary"
                                            size="sm"
                                            onClick={() => handleItemsClick(question)}
                                        >
                                            Gerenciar Itens
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteClick(question)}
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {questions.length === 0 && !loading && (
                <div className="text-center text-muted p-4">
                    Nenhuma questão registrada.
                </div>
            )}

            {/* Pagination */}
            {totalPages > 0 && (
                <PaginationComponent
                    page={currentPage}
                    size={pageSize}
                    total={totalElements}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    entityLabel="questões"
                />
            )}

            {/* Edit Question Modal */}
            <Modal show={showEditModal} onHide={handleEditModalClose}>
                <Modal.Header>
                    <Modal.Title>Editar Questão</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitEdit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Área</Form.Label>
                            <Form.Control
                                as="select"
                                name="areaId"
                                value={formData.areaId || ''}
                                onChange={handleInputChange}
                                required
                                disabled={areasLoading}
                            >
                                <option value="">
                                    {areasLoading ? 'Carregando áreas...' : 'Selecione uma área'}
                                </option>
                                {!areasLoading && areas.map(area => (
                                    <option key={area.area_id} value={area.area_id}>
                                        {area.nome}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Título</Form.Label>
                            <Form.Control
                                type="text"
                                name="titulo"
                                value={formData.titulo || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="descricao"
                                value={formData.descricao || ''}
                                onChange={handleInputChange}
                                rows={3}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleEditModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Salvar Alterações
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Question Modal */}
            <Modal show={showAddQuestionModal} onHide={handleAddQuestionModalClose}>
                <Modal.Header>
                    <Modal.Title>Adicionar Questão</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitAddQuestion}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Área</Form.Label>
                            <Form.Control
                                as="select"
                                name="areaId"
                                value={formData.areaId || ''}
                                onChange={handleInputChange}
                                required
                                disabled={areasLoading}
                            >
                                <option value="">
                                    {areasLoading ? 'Carregando áreas...' : 'Selecione uma área'}
                                </option>
                                {!areasLoading && areas.map(area => (
                                    <option key={area.area_id} value={area.area_id}>
                                        {area.nome}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Título</Form.Label>
                            <Form.Control
                                type="text"
                                name="titulo"
                                value={formData.titulo || ''}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="descricao"
                                value={formData.descricao || ''}
                                onChange={handleInputChange}
                                rows={3}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleAddQuestionModalClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Adicionar Questão
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                onConfirm={handleConfirmDelete}
                title="Confirmar Exclusão"
                message={`Tem certeza que deseja excluir a questão "${selectedQuestion?.titulo}"?`}
                warningMessage="Esta ação irá remover todos os itens e ações associados a esta questão."
                entityName="questão"
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default QuestionAdminPage;
